<template>
  <div class="mx-10 ma-5">
    <v-card flat class="pa-5">
      <div class="d-flex justify-end mb-4">
        <v-btn
          color="success"
          to="/GL-Transaction/Add"
          v-if="hasPermission('GL_Transactions.create')"
          >Add GL Transaction</v-btn
        >
      </div>
      <v-row>
        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="filters.gl_account"
            :items="gl_accounts"
            item-text="title"
            item-value="title"
            label="GL Account Title"
            outlined
            dense
            clearable
            :loading="isFetchingAccounts"
            :filter="() => true"
            @update:search-input="(searchTerm) => onSearchAccount(searchTerm)"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="filters.start_date"
            label="Start Date"
            type="date"
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="filters.end_date"
            label="End Date"
            type="date"
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn color="primary" @click="fetchTransactions">Filter</v-btn>
        </v-col>
      </v-row>

      <template v-if="transactions.length > 0">
        <DataTableWithPagination
          :headers="tableHeaders"
          :items="transactions"
          :loading="loading"
          :page="page"
          :total="total"
          @pageChange="page = $event"
        >
          <template #row="{ item, index }">
            <tr
              :class="[
                { 'first-row': index === 0 },
                index % 2 === 0 ? 'dark-row' : 'light-row',
                'custom-row',
              ]"
            >
              <td class="text-left">{{ item.ref_no }}</td>
              <td class="text-left">{{ item.gl_account }}</td>
              <td class="text-left">{{ item.cr }}</td>
              <td class="text-left">{{ item.dr }}</td>
              <td class="text-left">{{ formatDate(item.date, dateFormat) }}</td>
            </tr>
          </template>
        </DataTableWithPagination>
      </template>
      <template v-else>
        <v-alert type="info" class="mt-5" outlined>
          No GL transactions found for the applied filters.
        </v-alert>
      </template>
    </v-card>
  </div>
</template>

<script>
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { GLTransactionService } from "@/services/GLTransaction-service.js";
import { createNamespacedHelpers, mapGetters as rootMapGetters } from "vuex";
import { useDateFormatter } from "@/composables/useDateFormatter";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  components: { DataTableWithPagination },
  data() {
    return {
      filters: {
        gl_account: "",
        start_date: "",
        end_date: "",
      },
      transactions: [],
      page: 1,
      total: 0,
      loading: false,

      accountSearchTimeout: null,
      isFetchingAccounts: false,
      gl_accounts: [],

      headers: [
        { text: "Ref No", value: "ref_no" },
        { text: "GL Account", value: "gl_account" },
        { text: "CR", value: "cr", isCurrency: true },
        { text: "DR", value: "dr", isCurrency: true },
        { text: "Date", value: "date" },
      ],
      ...useDateFormatter(),
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    ...rootMapGetters("settings", ["dateFormat", "currencyCode"]),
    tableHeaders() {
      return this.headers.map((h) => {
        if (h.isCurrency) {
          return {
            ...h,
            text: `${h.text} (${this.currencyCode})`, // Add currency code automatically
          };
        }
        return h;
      });
    },
  },
  methods: {
    async fetchTransactions() {
      this.loading = true;
      try {
        const response = await GLTransactionService.listTransactions(
          {
            ...this.filters,
          },
          this.page
        );
        this.transactions = response.data?.data || [];
        this.total = response.data?.meta?.last_page || 0;
      } catch (error) {
        this.transactions = [];
        this.total = 0;
        console.error("Error fetching transactions:", error);
      } finally {
        this.loading = false;
      }
    },

    async onSearchAccount(searchTerm) {
      try {
        clearTimeout(this.accountSearchTimeout);
        this.isFetchingAccounts = true;
        this.accountSearchTimeout = setTimeout(async () => {
          const response = await GLTransactionService.getAccountsByTitle(
            searchTerm
          );
          this.gl_accounts = response.data.data.original;
        }, 300);
      } catch (error) {
        console.error("Error fetching accounts:", error);
      } finally {
        this.isFetchingAccounts = false;
      }
    },
  },
  mounted() {
    this.fetchTransactions();
  },
  watch: {
    page() {
      this.fetchTransactions();
    },
  },
};
</script>
